import { AsyncPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent, PwcsLayoutComponent, TileComponent, WatermarkComponent } from '@tecalisdev/infinity';
import { timer } from 'rxjs';
import { LanguageService } from 'src/app/core/services/language.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { IdentitySDKService } from 'src/app/shared/services/identity-sdk.service';
import { TextToSpeechService } from 'src/app/shared/services/text-to-speech.service';
import { IFRAME_RESULT } from '../../models/life-check';
import {
	IFinishOperation,
	IOperation,
	IProcessConfigResponse,
	LocalizedText,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PREMATURE_FINISH_ERROR_CODE,
} from '../../models/user-identification';
import { ProcessService } from '../../services/process.service';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import {
	resolveFinishResultMessage,
	resolvePendingFinishDescription,
} from '../../utils/resolve-finish-result-message.util';
import { resolveMatchDataErrorsUserMessage } from '../../utils/match-data-user-message.util';

@Component({
	selector: 'app-finish',
	imports: [
		TranslatePipe,
		AsyncPipe,
		NgClass,
		PwcsLayoutComponent,
		TileComponent,
		WatermarkComponent,
		ButtonComponent,
	],
	templateUrl: './finish.component.html',
	styleUrls: ['./finish.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class FinishComponent implements OnInit, AfterViewInit {
	@Input() operation: IFinishOperation;
	@Input() process?: IProcessConfigResponse;
	/** NIF-969: hay pasos incompletos; el padre debe recargar config y no quedarse en Finish vacío. */
	@Output() incompleteOperations = new EventEmitter<void>();
	private sendDataComplete: boolean = false;
	public IFRAME_RESULT = IFRAME_RESULT;
	public operationStatus = OPERATION_STATUS;
	public finishOperationStatus?: OPERATION_STATUS;
	public isPendingReview = false;
	public MODE = MODE;
	public shouldCheckDevice: boolean = false;
	/** NIF-1026: motivo KO desde POST /operation/finish (si enseñable). */
	private koReason: { title: string; message: string } | null = null;

	constructor(
		public designService: DesignService,
		private tts: TextToSpeechService,
		private processService: ProcessService,
		private errorService: ErrorService,
		private identitySDKService: IdentitySDKService,
		private cloudLogsService: CloudLogsService,
		private loadingService: LoadingService,
		private trans: TranslateService,
		private languageService: LanguageService,
	) {}

	ngOnInit(): void {
		this.shouldCheckDevice = Boolean(
			this.operation?.config?.redirect ||
				this.operation?.config?.redirect_ko ||
				this.operation?.config?.redirect_cancelled,
		);
		this.applyPresetFinishStatus();

		if (this.operation?.config?.iframe) {
			console.log('IFRAME');
		}
	}

	ngAfterViewInit(): void {
		if (this.shouldSkipFinishAsSecondary()) {
			return;
		}

		// NIF-969: no llamar finish; avisar al padre para recargar config y volver al paso pendiente.
		if (this.hasIncompleteMandatoryOperations()) {
			this.notifyIncompleteMandatoryOperations();
			return;
		}

		this.sendFinish(this.operation.config.iframe);
	}

	/** True si hay operaciones no-FINISH aún con `completed === false` en el proceso. */
	private hasIncompleteMandatoryOperations(): boolean {
		const ops: IOperation[] | undefined = this.process?.operations;
		if (!ops?.length) return false;
		return ops.some((op: IOperation) => op.type !== OPERATION_TYPE.FINISH && op.completed === false);
	}

	private notifyIncompleteMandatoryOperations(): void {
		this.cloudLogsService.sendCloudLogs(
			{ finish: 'Skipped POST /operation/finish: mandatory operations still incomplete' },
			'Finish - incomplete mandatory ops',
			LogType.Error,
		);
		// Diferir emit: evita NG0100 si el padre muta ops en el mismo ciclo de AfterViewInit.
		queueMicrotask(() => this.incompleteOperations.emit());
	}

	/**
	 * NIF-377: secondary con redirect — CTA para finalizar y redirigir en este dispositivo.
	 * El back promociona a primary al recibir POST /operation/finish.
	 */
	public continueOnThisDevice(): void {
		if (this.process) {
			this.process = { ...this.process, primary_device: true };
		}
		this.sendFinish(Boolean(this.operation?.config?.iframe));
	}

	/** Título OK: custom o i18n. */
	public okTitle(): string {
		return this.resolveMessage(this.operation?.config?.ok_title, 'FINISH.OK_TITLE');
	}

	public okDescription(): string {
		return this.resolveMessage(this.operation?.config?.ok_description, '');
	}

	public pendingTitle(): string {
		return this.resolveMessage(this.operation?.config?.pending_title, 'FINISH.PENDING_TITLE');
	}

	public pendingDescription(): string {
		return resolvePendingFinishDescription(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			this.operation?.config?.pending_title,
			this.operation?.config?.pending_description,
		);
	}

	public koTitle(): string {
		if (this.koReason?.title) {
			return this.koReason.title;
		}
		return this.resolveMessage(this.operation?.config?.ko_title, 'FINISH.KO_TITLE');
	}

	public koDescription(): string {
		if (this.koReason?.message) {
			return this.koReason.message;
		}
		return this.resolveMessage(this.operation?.config?.ko_description, '');
	}

	public cancelledTitle(): string {
		return this.resolveMessage(this.operation?.config?.cancelled_title, 'FINISH.CANCELLED_TITLE');
	}

	public cancelledDescription(): string {
		return this.resolveMessage(this.operation?.config?.cancelled_description, 'FINISH.CANCELLED_DESCRIPTION');
	}

	/**
	 * NIF-377: secondary con redirect — no cortar el poll del padre ni POST /operation/finish.
	 * Si el primary cierra, /status devolverá estado definitivo y el padre actualizará la UI.
	 */
	private shouldSkipFinishAsSecondary(): boolean {
		if (!(this.shouldCheckDevice && !this.process?.primary_device)) {
			return false;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				primaryDevice: this.process?.primary_device ?? false,
				shouldCheckDevice: this.shouldCheckDevice,
			},
			'Finish - skip secondary',
			LogType.Warning,
		);
		return true;
	}

	private sendFinish(isIframe: boolean): void {
		this.processService.unSubcribeCheckStatus();
		if (this.shouldSkipFinishAsSecondary()) {
			return;
		}
		// NIF-969: defensa también en CTA «Seguir en este dispositivo».
		if (this.hasIncompleteMandatoryOperations()) {
			this.notifyIncompleteMandatoryOperations();
			return;
		}

		const loadingText = this.resolveMessage(this.operation?.config?.loading_text, 'FINISH.LOADING_TITLE');
		isIframe ? this.loadingService.showIframe(loadingText) : this.loadingService.show(loadingText);

		this.processService.finishOperation().subscribe({
			next: (response) => {
				this.hideFinishLoading(isIframe);
				const status = this.resolveFinishStatus(response.status);
				this.isPendingReview = response.pending_review === true;
				this.applyKoReasonFromFinishResponse(response.ko_reason);

				if (status !== OPERATION_STATUS.OK && status !== OPERATION_STATUS.KO) return;

				// Locución: siempre claves i18n por defecto (NIF-762).
				this.speakOperationResult(status);

				if (isIframe) {
					timer(1000).subscribe(() => {
						if (!this.sendDataComplete) {
							this.sendDataIframe(status === 'OK' ? IFRAME_RESULT.OK : IFRAME_RESULT.KO);
							// Notificar FLOW_COMPLETE al SDK antes de completar el iframe
							this.notifyFlowComplete(status);
						}
					});
				} else {
					timer(3000).subscribe(() => {
						// Notificar FLOW_COMPLETE al SDK antes del redirect
						this.notifyFlowComplete(status);

						if (status === OPERATION_STATUS.OK && this.operation.config.redirect) {
							window.location.href = this.operation.config.redirect;
						}
						if (status === OPERATION_STATUS.KO && this.operation.config.redirect_ko) {
							window.location.href = this.operation.config.redirect_ko;
						}
					});
				}
			},
			error: (error: HttpErrorResponse) => {
				this.hideFinishLoading(isIframe);
				const code = Number(error?.error?.error?.error_code);
				if (code === 1005) {
					// NIF-960: carrera tab switch / doble finish → proceso ya cerrado OK.
					this.finishOperationStatus = OPERATION_STATUS.OK;
					this.cloudLogsService.sendCloudLogs(
						{ errorFinishOperation: '1005 treated as already finished OK' },
						'Finish - finish already done',
						LogType.Warning,
					);
					return;
				}
				this.cloudLogsService.sendCloudLogs(
					{ errorFinishOperation: error },
					'Finish - error finishOperation',
					LogType.Error,
				);
				// NIF-969: 1120 → recargar config / volver al paso pendiente (no error genérico).
				if (this.isPrematureFinishError(error)) {
					this.notifyIncompleteMandatoryOperations();
					return;
				}
				this.errorService.hasGeneralError({ ...error?.error });
			},
		});
	}

	private isPrematureFinishError(error: unknown): boolean {
		const body = (error as { error?: { error?: { error_code?: number }; error_code?: number } })?.error;
		const code = Number(body?.error?.error_code ?? body?.error_code);

		return code === PREMATURE_FINISH_ERROR_CODE;
	}

	private resolveMessage(value: LocalizedText | null | undefined, fallbackI18nKey: string): string {
		return resolveFinishResultMessage(
			(v, opts) => this.languageService.resolveLocalizedText(v, opts),
			(key) => this.trans.instant(key),
			value,
			fallbackI18nKey,
		);
	}

	private applyPresetFinishStatus(): void {
		const preset = this.operation?.config?.status;
		if (preset === OPERATION_STATUS.OK || preset === OPERATION_STATUS.KO || preset === OPERATION_STATUS.CANCELLED) {
			this.finishOperationStatus = preset;
		}
	}

	/** NIF-1026: copy enseñable viene del back en finish; no de cada operación. */
	private applyKoReasonFromFinishResponse(
		koReason:
			| {
					title?: string;
					message?: string;
					/** NIF-1101: keys de cotejo → i18n journey. */
					match_data_errors?: Array<{ key?: string | null }> | null;
			  }
			| null
			| undefined,
	): void {
		if (!koReason || (typeof koReason.title !== 'string' && typeof koReason.message !== 'string')) {
			this.koReason = null;
			return;
		}

		// NIF-1101: si hay keys, el copy concreto sale de i18n (catálogo 2014 = fallback genérico).
		const fieldMsg = resolveMatchDataErrorsUserMessage(koReason.match_data_errors, (key, params) =>
			this.trans.instant(key, params),
		);
		if (fieldMsg) {
			this.koReason = { title: fieldMsg, message: fieldMsg };
			return;
		}

		this.koReason = {
			title: typeof koReason.title === 'string' ? koReason.title : '',
			message: typeof koReason.message === 'string' ? koReason.message : '',
		};
	}

	/** KO pre-seteado por verificationFail prevalece sobre API OK (NIF-553). */
	private resolveFinishStatus(apiStatus: OPERATION_STATUS): OPERATION_STATUS {
		const preset = this.operation?.config?.status;
		if (preset === OPERATION_STATUS.KO) {
			this.finishOperationStatus = OPERATION_STATUS.KO;
			return OPERATION_STATUS.KO;
		}
		this.finishOperationStatus = apiStatus;
		return apiStatus;
	}

	private hideFinishLoading(isIframe: boolean): void {
		isIframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	/*
	 * Iframe
	 */
	public sendDataIframe(status: IFRAME_RESULT): void {
		if (!this.sendDataComplete) {
			this.sendDataComplete = true;
			console.log(`PostMessage: ${status}`);
			window.top.postMessage({ code: 'life-test', status: status }, '*');
		}
	}

	private speakOperationResult(status: OPERATION_STATUS): void {
		if (this.isPendingReview) {
			this.tts.speak('FINISH.OK_PENDING_TITLE');
			return;
		}
		if (status === OPERATION_STATUS.OK) {
			this.tts.speak('FINISH.OK_TITLE');
		} else if (status === OPERATION_STATUS.KO) {
			this.tts.speak('FINISH.KO_TITLE');
		}
	}

	/**
	 * Notifica al SDK que el flujo completo ha terminado
	 * El servicio se encarga de controlar que solo se notifique una vez
	 */
	private notifyFlowComplete(status: OPERATION_STATUS): void {
		this.identitySDKService.notifyFlowComplete(status, {
			process: this.process?.process,
		});
	}
}
